import Footer from '../components/Footer';
import Contact from '../components/Contact';
import Header from '../components/Header';
import SeoHelmet from '../components/SeoHelmet';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug, currentLanguage]);

  const loadArticle = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/articles/${slug}?language=${currentLanguage}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'RU' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">{t('loading') || 'Загрузка...'}</div>
      </div>
        <Contact />
      <Footer />
    </>
  );
  }

  if (!article) {
    return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'RU' ? 'Статья не найдена' : 'Article not found'}
          </h2>
          <button
            onClick={() => navigate('/articles')}
            className="text-orange-500 hover:text-orange-600 font-medium"
          >
            {currentLanguage === 'RU' ? '← Вернуться к статьям' : '← Back to articles'}
          </button>
        </div>
      </div>
        <Contact />
      <Footer />
    </>
  );
  }

  return (
    <>
      <SeoHelmet
        title={`${article.title} | ${currentLanguage === 'EN' ? 'VELOX HINT News' : 'Новости ВЕЛОКС ХИНТ'}`}
        description={article.preview || article.title}
      />
      <Header />
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/articles')}
          className="text-orange-500 hover:text-orange-600 font-medium mb-8 inline-flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {currentLanguage === 'RU' ? 'Вернуться к статьям' : 'Back to articles'}
        </button>

        {/* Article Content */}
        <article className="bg-white rounded-lg shadow-sm p-8">
          {/* Header */}
          <header className="mb-8">
            <div className="text-sm text-gray-500 mb-4">
              {formatDate(article.published_date)}
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{article.title}</h1>
          </header>

          {/* Featured Image */}
          {article.image_url && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Video */}
          {article.video_url && (
            <div className="mb-8">
              <div className="relative w-full rounded-lg overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={article.video_url.replace('watch?v=', 'embed/')}
                  title={article.title}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>
      </div>
    </div>
      <Contact />
      <Footer />
    </>
  );
};

export default ArticleDetail;